import { $api } from 'views/services/interceptor';
import {
  IInvoiceConfigMeta,
  IInvoiceCountry,
  IInvoiceFeeRule,
  IInvoicePaymentDetail,
  IInvoicePaymentList,
  IInvoiceRouteRule,
  IInvoiceSector,
  IInvoiceSectorList,
  IInvoiceSender,
  IInvoiceSenderAccount,
  IInvoiceSettings,
} from 'views/types/invoicePayments';

const BASE = '/api/admin/invoice-payments';
const CONFIG = '/api/admin/invoice-payment-config';

const invoicePaymentsApi = () => ({
  // --- Review / finance ---
  getList: async (params: {
    page: number;
    limit: number;
    status?: string;
    search?: string;
  }) => {
    const { data } = await $api.get<IInvoicePaymentList>(BASE, { params });
    return data;
  },
  getById: async (id: string) => {
    const { data } = await $api.get<IInvoicePaymentDetail>(`${BASE}/${id}`);
    return data;
  },
  downloadDocument: async (
    requestId: string,
    documentId: string,
    fileName: string
  ) => {
    const { data } = await $api.get(
      `${BASE}/${requestId}/documents/${documentId}`,
      { responseType: 'blob' }
    );
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  },
  approve: async (id: string) => $api.post(`${BASE}/${id}/approve`),
  reject: async (id: string, reason: string) =>
    $api.post(`${BASE}/${id}/reject`, { reason }),
  requestDocuments: async (id: string, note: string) =>
    $api.post(`${BASE}/${id}/request-documents`, { note }),
  returnToReview: async (id: string) =>
    $api.post(`${BASE}/${id}/return-to-review`),
  moveToProcessing: async (id: string) => $api.post(`${BASE}/${id}/processing`),
  uploadProof: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return $api.post(`${BASE}/${id}/proof`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  complete: async (id: string) => $api.post(`${BASE}/${id}/complete`),
  cancel: async (id: string, reason?: string) =>
    $api.post(`${BASE}/${id}/cancel`, { reason }),
  markFailed: async (id: string, reason: string) =>
    $api.post(`${BASE}/${id}/fail`, { reason }),
  retry: async (id: string) => $api.post(`${BASE}/${id}/retry`),
  updateKyb: async (
    userId: string,
    payload: {
      clientType?: string;
      kybStatus?: string;
      invoicePaymentEnabled?: boolean;
    }
  ) => $api.post(`${BASE}/users/${userId}/kyb`, payload),

  // --- Config ---
  getMeta: async () => {
    const { data } = await $api.get<IInvoiceConfigMeta>(`${CONFIG}/meta`);
    return data;
  },
  getSenders: async () => {
    const { data } = await $api.get<IInvoiceSender[]>(`${CONFIG}/senders`);
    return data;
  },
  createSender: async (payload: Partial<IInvoiceSender>) => {
    const { data } = await $api.post<IInvoiceSender>(
      `${CONFIG}/senders`,
      payload
    );
    return data;
  },
  updateSender: async (id: string, payload: Partial<IInvoiceSender>) => {
    const { data } = await $api.patch<IInvoiceSender>(
      `${CONFIG}/senders/${id}`,
      payload
    );
    return data;
  },
  createSenderAccount: async (
    senderId: string,
    payload: Partial<IInvoiceSenderAccount>
  ) => {
    const { data } = await $api.post<IInvoiceSenderAccount>(
      `${CONFIG}/senders/${senderId}/accounts`,
      payload
    );
    return data;
  },
  updateSenderAccount: async (
    id: string,
    payload: Partial<IInvoiceSenderAccount>
  ) => {
    const { data } = await $api.patch<IInvoiceSenderAccount>(
      `${CONFIG}/accounts/${id}`,
      payload
    );
    return data;
  },
  getFeeRules: async () => {
    const { data } = await $api.get<IInvoiceFeeRule[]>(`${CONFIG}/fee-rules`);
    return data;
  },
  createFeeRule: async (payload: Partial<IInvoiceFeeRule>) => {
    const { data } = await $api.post<IInvoiceFeeRule>(
      `${CONFIG}/fee-rules`,
      payload
    );
    return data;
  },
  updateFeeRule: async (id: string, payload: Partial<IInvoiceFeeRule>) => {
    const { data } = await $api.patch<IInvoiceFeeRule>(
      `${CONFIG}/fee-rules/${id}`,
      payload
    );
    return data;
  },
  deleteFeeRule: async (id: string) => $api.delete(`${CONFIG}/fee-rules/${id}`),
  getRouteRules: async () => {
    const { data } = await $api.get<IInvoiceRouteRule[]>(
      `${CONFIG}/route-rules`
    );
    return data;
  },
  createRouteRule: async (payload: Partial<IInvoiceRouteRule>) => {
    const { data } = await $api.post<IInvoiceRouteRule>(
      `${CONFIG}/route-rules`,
      payload
    );
    return data;
  },
  updateRouteRule: async (id: string, payload: Partial<IInvoiceRouteRule>) => {
    const { data } = await $api.patch<IInvoiceRouteRule>(
      `${CONFIG}/route-rules/${id}`,
      payload
    );
    return data;
  },
  deleteRouteRule: async (id: string) =>
    $api.delete(`${CONFIG}/route-rules/${id}`),
  getCountries: async (params?: { listType?: string; search?: string }) => {
    const { data } = await $api.get<IInvoiceCountry[]>(`${CONFIG}/countries`, {
      params,
    });
    return data;
  },
  createCountry: async (payload: Partial<IInvoiceCountry>) => {
    const { data } = await $api.post<IInvoiceCountry>(
      `${CONFIG}/countries`,
      payload
    );
    return data;
  },
  updateCountry: async (id: string, payload: Partial<IInvoiceCountry>) => {
    const { data } = await $api.patch<IInvoiceCountry>(
      `${CONFIG}/countries/${id}`,
      payload
    );
    return data;
  },
  deleteCountry: async (id: string) => $api.delete(`${CONFIG}/countries/${id}`),
  getSectors: async (params: {
    search?: string;
    riskTier?: string;
    page?: number;
    limit?: number;
  }) => {
    const { data } = await $api.get<IInvoiceSectorList>(`${CONFIG}/sectors`, {
      params,
    });
    return data;
  },
  createSector: async (payload: Partial<IInvoiceSector>) => {
    const { data } = await $api.post<IInvoiceSector>(
      `${CONFIG}/sectors`,
      payload
    );
    return data;
  },
  updateSector: async (id: string, payload: Partial<IInvoiceSector>) => {
    const { data } = await $api.patch<IInvoiceSector>(
      `${CONFIG}/sectors/${id}`,
      payload
    );
    return data;
  },
  deleteSector: async (id: string) => $api.delete(`${CONFIG}/sectors/${id}`),
  getSettings: async () => {
    const { data } = await $api.get<IInvoiceSettings>(`${CONFIG}/settings`);
    return data;
  },
  updateSettings: async (payload: Partial<IInvoiceSettings>) => {
    const { data } = await $api.patch<IInvoiceSettings>(
      `${CONFIG}/settings`,
      payload
    );
    return data;
  },
});

export const invoicePaymentsService = invoicePaymentsApi();
